import { ethers, BigNumber } from 'ethers';
import create from 'zustand';
import { Position } from '../../types';
import { mainnet } from '../../components/eth/Addresses';

export interface UserStore {
  userAddress?: string;
  provider?: ethers.providers.Provider;
  signer?: ethers.Signer;
  balanceEth?: string;
  balanceLvusd?: string;
  balanceArch?: string;
  balanceArchIn18Dec?: BigNumber;
  balanceOusd?: string;
  balanceOusdIn18Dec?: BigNumber;
  network?: string;
  networkID?: string;
  positions?: Position[];
  balanceUsdt?: number;
  balanceUsdc?: number;
  balanceDai?: number;
}

export interface DappStore {
  isAppInitialized: boolean;
  isProcessingModalOpen?: boolean;
  isViewNft?: boolean;
  isWrongNetwork?: boolean;
  isClosePositionModal?: boolean;
  isClosingPositionModal?: boolean;
  isCloseConfirmationModal?: boolean;
  activePositionId?: number;
  leverageInterval?: number;
  archLvusdRatioInterval?: number;
  archPriceInterval?: number;
  archPrice?: number;
  latestTx?: ethers.Transaction & { transactionHash: string };
  // TODO: Research on Error types
  currentError?: any;
  bodyScroll?: boolean;
  // TODO?: Research on contracts types
  contracts?: any;
  addresses?: any;
  leverageMultiplier?: number;
  leveragedOusdApy?: number;
  finalOusdApy?: number;
  archMultiplier?: number;
  numOfCycles?: number;
  originationFee?: string;
  performanceFee?: string;
  lvUSDToArchRatio?: string;
  lvUSDToArchRatioIn18Dec?: BigNumber;
  // setLvUSDToArchRatio?: (value; valueIn18Dec) => {
  //   set((state) => {
  //     return { lvUSDToArchRatio?: value; lvUSDToArchRatioIn18Dec?: valueIn18Dec };
  //   });
  // };
  leverageAvailable?: string;
  leverageAvailableIn18Dec?: BigNumber;
  // setLeverageAvailable?: (value; valueIn18Dec) => {
  //   set((state) => {
  //     return { leverageAvailable?: value; leverageAvailableIn18Dec?: valueIn18Dec };
  //   });
  // };
  ousdAPY?: number;
  lpAPY?: number;
  ltAPY?: number;
  tvl?: number;
  showLegalDisclaimerModal: boolean;
  ousdCollateralMinimum?: string;
  showUnavailableClosingPositionModal?: boolean;
  showRouteAndFeesModal?: boolean;
  positionLifetimeInDays?: number;
  balancesLoaded?: boolean;
  archApprovalLoaded?: boolean;
  stableCoinApprovalsLoaded?: boolean;
  contractsLoaded?: boolean;
  minimumDepositIncreased?: number;
  activePosition?: Position;
  modalProps?: any;
}

export interface PositionStore {
  isArchApproved: boolean;
  isOusdApproved: boolean;
  isUsdtApproved: boolean;
  isUsdcApproved: boolean;
  isDaiApproved: boolean;
  approvedUsdt: string;
  approvedUsdc: string;
  approvedDai: string;
  approvedArch: string;
  approvedArchIn18Dec: BigNumber;
  approvedOusd: string;
  approvedOusdIn18Dec: BigNumber;
  openPositionPrincipal: number;
  openPositionPrincipalIn18Dec: BigNumber;
  openPositionDebt: number;
  openPositionDebtIn18Dec: BigNumber;
  openPositionArchNeeded: number;
  openPositionArchNeededIn18Dec: BigNumber;
  openPositionArchToBurn: number;
  openPositionArchToBurnIn18Dec: BigNumber;
  setOpenPositionPrincipal?: (value: number, valueIn18Dec: BigNumber) => void;
  setOpenPositionDebt?: (value: number, valueIn18Dec: BigNumber) => void;
  setOpenPositionArchNeeded?: (value: number, valueIn18Dec: BigNumber) => void;
  setOpenPositionArchToBurn?: (value: number, valueIn18Dec: BigNumber) => void;
}

// State for the user/metamask account
const userStore = create<UserStore>(() => ({}));

// State for smart contracts & front-end elements
const dappStore = create<DappStore>(() => ({
  isAppInitialized: false,
  showLegalDisclaimerModal: true,
  /// TODO set to true for development
  showRouteAndFeesModal: false,
  addresses: {
    ...mainnet,
  },
  numOfCycles: 12,
}));

// State for current OpenPosition flow
const positionStore = create<PositionStore>((set) => ({
  isArchApproved: false,
  isOusdApproved: false,
  isUsdtApproved: false,
  isUsdcApproved: false,
  isDaiApproved: false,
  approvedUsdt: '0',
  approvedUsdc: '0',
  approvedDai: '0',
  approvedArch: '0',
  approvedArchIn18Dec: BigNumber.from(0),
  approvedOusd: '0',
  approvedOusdIn18Dec: BigNumber.from(0),
  openPositionPrincipal: 0,
  openPositionPrincipalIn18Dec: BigNumber.from(0),
  setOpenPositionPrincipal: (value: number, valueIn18Dec: BigNumber) => {
    set((state) => {
      return { openPositionPrincipal: value, openPositionPrincipalIn18Dec: valueIn18Dec };
    });
  },
  openPositionDebt: 0,
  openPositionDebtIn18Dec: BigNumber.from(0),
  setOpenPositionDebt: (value: number, valueIn18Dec: BigNumber) => {
    set((state) => {
      return { openPositionDebt: value, openPositionDebtIn18Dec: valueIn18Dec };
    });
  },
  openPositionArchNeeded: 0,
  openPositionArchNeededIn18Dec: BigNumber.from(0),
  setOpenPositionArchNeeded: (value: number, valueIn18Dec: BigNumber) => {
    set((state) => {
      return {
        openPositionArchNeeded: value,
        openPositionArchNeededIn18Dec: valueIn18Dec,
      };
    });
  },
  openPositionArchToBurn: 0,
  openPositionArchToBurnIn18Dec: BigNumber.from(0),
  setOpenPositionArchToBurn: (value: number, valueIn18Dec: BigNumber) => {
    set((state) => {
      return { openPositionArchToBurn: value, openPositionArchToBurnIn18Dec: valueIn18Dec };
    });
  },
}));

export { userStore, dappStore, positionStore };
